import type { HistoryEntry } from '@ctcells/contract'
import { enumLabel, hasMessage, t } from '@ctcells/i18n'

import type { Column } from '../../atoms/DataTable.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { errorMessageOf, orDash, timeSecText } from '../format.ts'

import { detailText, humanReason, operationText } from './batchDetailModel.ts'

/**
 * 「이벤트」 원장 둘(`GET /history?event_type=event` · `…=batch_record` — `@ctcells/api` `useEventHistory`)의 **행 모양 한 벌.**
 *
 * 🔴 **두 원장의 `detail` 모양이 다르다**(`ctcells-core` `app/services/history.py` `_event_select` · `_batch_record_select`).
 *    | 원장 | 동작 낱말 | 그 밖 |
 *    | `event` | `operation` | `error_message` |
 *    | `batch_record` | **`kind`** | `module_id` · `execution_id` · `command_id` |
 *    | `audit` | `operation`(`startBatch` …) | `action` · `reason`(사람 사유 또는 서버 합성 채움말) · 행 `actor` |
 *    감사 행은 상시 작업 줄 · 작업별 CSV 에만 섞인다(`@ctcells/api` `BATCH_EVENT_LEDGERS` · 팀장 결정 2026-10-11) — 사람이 누른
 *    시작 · 중지는 그 원장에만 있다. 「종류」 칸은 `category` 가 없어 원장 이름(「감사」)을 적는다
 *    `batch_record.kind` 는 **종전 `event_log.operation` 낱말 그대로**다(서버 `batch_dispatch._write_event` — 「부르는 쪽이
 *    넘기는 `operation` 은 표의 `kind` 다」). 그래서 「동작」 열은 둘을 한 칸에 읽는다(`ledgerOperation`).
 *
 * 🔴 **작업 상세의 이벤트 탭과 상시 작업 줄의 이벤트 칸이 같이 쓴다**(2026-09-28). 같은 원장을
 *    두 곳에서 다른 모양으로 그리면 「같은 사건이 왜 다르게 보이나」가 된다 — 열은 여기서만 고친다.
 *
 * 🔴 **`detail.message` 를 읽지 마라.** 서버가 그 키를 만들지 않는다 — 문구의 권위는 **에러 코드
 *    사전**이다(`app/services/history.py` `_event_select`).
 * 🔴 **「상관 ID」 열을 되살리지 마라.** 응답이 싣긴 하지만 실측상 거의 늘 비어 있다.
 */
export function eventColumns(): Column<HistoryEntry>[] {
  return [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      cell: (row) => <span className="muted mono num">{timeSecText(row.ts)}</span>,
    },
    {
      key: 'severity',
      header: t('column.severity'),
      nowrap: true,
      cell: (row) => {
        const severity = detailText(row, 'severity')
        return severity === undefined ? (
          <span className="muted">{t('mask.none')}</span>
        ) : (
          <StatusBadge label={enumLabel('enum.severity', severity)} status={severity} />
        )
      },
    },
    {
      key: 'category',
      header: t('column.kind'),
      nowrap: true,
      cell: (row) => <span className="muted">{orDash(categoryText(row))}</span>,
    },
    {
      key: 'operation',
      header: t('column.operation'),
      nowrap: true,
      cell: (row) => <span className="mono">{operationText(ledgerOperation(row))}</span>,
    },
    {
      key: 'message',
      header: t('column.content'),
      cell: (row) => eventContentText(row),
    },
  ]
}

/**
 * 「내용」 열 — **그 행이 실제로 말하는 것**(eric 2026-09-28 「상태가 아니라 스텝별 내용을 보여줄 수 없어?」).
 *
 * 1. 에러 코드가 있으면 코드 사전 문구(없으면 코드가 곧 정보다)
 * 2. `context.from`·`context.to` 가 있으면 **「이전 → 다음」**(`module_state_change` · 안전 상태 전이).
 *    실측 원장의 대부분이 이것이다 — 전에는 이 값을 안 읽어 「—」 였다
 *    배치 전이(`batch_record` 의 `batch_state_change`)는 작업 상태 이름으로 읽는다
 * 3. `dispatch_send` 는 보낸 스텝 수(`context.items`), `execution_observed` 는 그 모듈(`context.module_id` —
 *    `batch_record` 행은 `detail.module_id` 칸이다)
 * 4. 진행 사본(`batch_record` 의 `progress_record`)은 서버가 지은 문장 `context.first_result.said`
 *    (실물 dev2 2026-10-11 — 「보냈고 모듈이 받았다」 · 「보냈는데 «답을 못 받았다» …」). ⚠ 실물 10행 중 8행은 그 문장이
 *    없다(`first_result` 가 `handoff` · `point_id` · `status` 같은 장부 값뿐) — 그 행은 빈 칸이다. 장부 낱말을 풀어 쓰지 않는다
 *    실행 종착(`execution_terminal` — 서버 `batch_dispatch.EXECUTION_TERMINAL_OPERATION` · 실행마다 한 행)은 모듈이 말한
 *    종착 사유 `context.reason`(실물 「실행 완료 — 스텝 177개」) · 없으면 `context.status`(complete · error · cancelled)의 이름.
 *    실패 종착은 위 1(에러 코드)이 먼저 잡는다
 * 5. 서버가 지은 사유 문장(`detail.reason` — 멈춤 사유 · 재개 거부)이 있으면 그 문장
 * 6. 감사 행(`audit`)은 사람이 적은 사유(`humanReason` — 서버 합성 채움말 `operation=… target=…` 은 버린다) ·
 *    없으면 누가 눌렀나(행 `actor`)
 * 7. 그 밖은 **빈 칸**. 「—」 를 찍지 않는다 — 비어 있는 게 곧 「더 말할 것이 없다」다
 *
 * 🔴 상태 이름은 사전(`enum.moduleState.*`)을 먼저 보고 **모르는 값은 원문**이다 — `enumLabel` 은
 *    모르는 것을 `—` 로 떨어뜨려 「무엇에서 무엇으로」가 사라진다.
 * 🔴 `detail.message` 를 읽지 않는다(서버가 만들지 않는다).
 */
export function eventContentText(row: HistoryEntry): string {
  if (row.event_type === 'audit') return auditContentText(row)
  const code = detailText(row, 'error_code')
  if (code !== undefined) return errorMessageOf(code, code)
  const context = contextOf(row)
  const operation = ledgerOperation(row)
  const from = textOf(context?.['from'])
  const to = textOf(context?.['to'])
  if (from !== undefined && to !== undefined) {
    const name = operation === BATCH_STATE_CHANGE ? batchStateName : stateName
    return t('event.content.stateChange', { from: name(from), to: name(to) })
  }
  if (operation === 'dispatch_send' && typeof context?.['items'] === 'number')
    return t('event.content.dispatchItems', { count: context['items'] })
  const moduleId = textOf(context?.['module_id']) ?? detailText(row, 'module_id')
  if (operation === 'execution_observed' && moduleId !== undefined)
    return t('event.content.executionObserved', { module: moduleId })
  if (operation === 'execution_terminal') {
    const reason = textOf(context?.['reason'])
    if (reason !== undefined) return reason
    const status = textOf(context?.['status'])
    if (status !== undefined) {
      const key = `enum.executionEvent.${status}`
      return hasMessage(key) ? t(key) : status
    }
  }
  if (operation === 'progress_record') {
    const said = textOf(recordOf(context?.['first_result'])?.['said'])
    if (said !== undefined) return said
  }
  return detailText(row, 'reason') ?? ''
}

/** 감사 행의 「내용」 — 사람 사유가 먼저, 없으면 행위자. 합성 채움말은 다른 칸에 이미 있는 값뿐이라 싣지 않는다 */
function auditContentText(row: HistoryEntry): string {
  const reason = humanReason(row)
  if (reason !== undefined && reason !== '') return reason
  return row.actor == null || row.actor === ''
    ? ''
    : t('event.content.byActor', { actor: row.actor })
}

/** 「종류」 칸 — `category`. 감사 행은 그 칸이 없어 원장 이름(「감사」)이다 */
function categoryText(row: HistoryEntry): string | undefined {
  return row.event_type === 'audit'
    ? enumLabel('enum.historyType', 'audit')
    : detailText(row, 'category')
}

/**
 * 사람이 누른 작업 전이의 감사 `operation`(계약 `operationId`) — 「상태 변경」 갈래가 이것을 상태 변경으로 센다.
 * 작업 생성(`createBatch`)은 전이가 아니라 뺀다. 서버 `views/batches.py` · `scheduler_tick` 의 `transition_audit` 호출 낱말.
 */
const BATCH_TRANSITION_OPERATIONS: ReadonlySet<string> = new Set([
  'startBatch',
  'pauseBatch',
  'resumeBatch',
  'stopBatch',
  'recoverBatch',
])

/** 배치 전이 기록의 낱말(서버 `batches.STATE_CHANGE_OPERATION`) — `context.from`·`to` 가 작업 상태다 */
const BATCH_STATE_CHANGE = 'batch_state_change'

/**
 * 행의 **동작 낱말** — `event` 원장은 `detail.operation`, `batch_record` 원장은 `detail.kind`(같은 낱말 축 · 위 머리말).
 * 🔴 원장으로 가른다 — `batch_record` 가 아닌 행의 `kind` 는 읽지 않는다(다른 뜻일 수 있다).
 */
export function ledgerOperation(row: HistoryEntry): string | undefined {
  return row.event_type === 'batch_record' ? detailText(row, 'kind') : detailText(row, 'operation')
}

/** 작업 상태 원값 → 한글. 사전에 없으면 원문 */
function batchStateName(value: string): string {
  const key = `enum.batchStatus.${value}`
  return hasMessage(key) ? t(key) : value
}

/** 상태 원값 → 한글. 사전에 없으면 원문(모르는 것을 지우지 않는다) */
export function stateName(value: string): string {
  const key = `enum.moduleState.${value}`
  return hasMessage(key) ? t(key) : value
}

/** 원장 행이 **상태 변경**인가 — 이벤트 칸 갈래(「상태 변경」)가 쓴다. 사람이 누른 작업 전이(감사)도 든다 */
export function isStateChangeEvent(row: HistoryEntry): boolean {
  if (row.event_type === 'audit')
    return BATCH_TRANSITION_OPERATIONS.has(detailText(row, 'operation') ?? '')
  const context = contextOf(row)
  return textOf(context?.['from']) !== undefined && textOf(context?.['to']) !== undefined
}

function contextOf(row: HistoryEntry): Record<string, unknown> | undefined {
  return recordOf(row.detail?.['context'])
}

function recordOf(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function textOf(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

/** 치명·오류 행은 왼쪽 굵은 띠(`DataTable.emphasis`) */
export function isEmphasisEvent(row: HistoryEntry): boolean {
  const severity = detailText(row, 'severity')
  return severity === 'CRITICAL' || severity === 'ERROR'
}

/** 원장이 다르면 같은 시각 · 같은 낱말이어도 다른 행이다 — 원장을 키에 싣는다 */
export function eventRowKey(row: HistoryEntry): string {
  return `${row.event_type}-${row.ts}-${row.target ?? ''}-${ledgerOperation(row) ?? ''}`
}
